import axios from 'axios'
import i18n from '../i18n/index'

export default {
    getList(userId, { fields, limit, offset, search } = {}) {
        const _fields = (
            fields || [
                'id',
                'firstName',
                'lastName',
                'dateOfBirth',
                'address',
                'city',
                'state',
                'zip',
                'mobilePhone',
                'email',
                'ssn',
                'profilePicture',
                'mcard(id)',
                'lastReferralCreatedDateTime',
            ]
        ).join(',')
        return axios.get(`/patient/user/${userId}`, {
            params: { _fields, limit, offset, search },
        })
    },

    getPatientDetails(patientId, userId, { fields } = {}) {
        const _fields = (
            fields || [
                'id',
                'firstName',
                'lastName',
                'dateOfBirth',
                'address',
                'city',
                'state',
                'zip',
                'floorNumber',
                'apartmentNumber',
                'buildingNumber',
                'mobilePhone',
                'homePhone',
                'email',
                'ssn',
                'profilePicture',
                'mcard(allergies,gender,height,weight,primaryInsuranceTypeId,secondaryInsuranceTypeId,memberId,groupNumber,emergencyContact,educationalLevel,isEmployed,occupation,maritalStatus,numberOfChildren,haveMother,mothersBirthYear,mothersYearIfPassed,haveFather,fathersBirthYear,fathersYearIfPassed,personalMedicalConditions,familyMedicalConditions,proceduresOrSurgeries,chronicConditions,medications,vaccines,primaryCareProvider,preferredFarmacy,preferredLanguage,preferredContactMethod)',
                'lastReferralCreatedDateTime',
            ]
        ).join(',')
        return axios.get(`/patient/${patientId}/user/${userId}`, {
            params: { _fields },
        })
    },

    removePatient(patientId, userId) {
        return axios.put(`/patient/${patientId}/user/${userId}/remove-connection`)
    },

    uploadPatientFile(userId, file) {
        return axios.post(`patient/user/${userId}/import`, {
            file: file.base64File,
        })
    },

    updatePatientDetails(patientId, userId, data) {
        return axios.put(`/patient/${patientId}/user/${userId}`, data)
    },

    getPatientRecords(patientId, userId, { fields } = {}) {
        const _fields = (fields || ['id', 'fileName', 'note', 'extension', 'url']).join(',')
        return axios.get(`/patient/${patientId}/user/${userId}/get-records`, {
            params: { _fields },
        })
    },

    add(userId, data) {
        return axios.post(`/patient/user/${userId}`, data)
    },

    uploadPatientRecord(patientId, userId, files) {
        return Promise.all(
            files.map((file) => {
                const body = file.uploadKey
                    ? {
                          note: file.file.name,
                          uploadKey: file.uploadKey,
                          fileName: file.file.name,
                          size: Math.round((file.fileSize || file.file.size) / 1024),
                          patientId,
                          userId,
                      }
                    : {
                          note: file.file.name,
                          file: file.base64File,
                          patientId,
                          userId,
                      }

                return axios
                    .post(`/patient/${patientId}/user/${userId}/upload-records`, body, {
                        onUploadProgress: function (progressEvent) {
                            if (!progressEvent.total || file.uploadKey) return
                            var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                            file.progress = percentCompleted
                        },
                    })
                    .then((res) => {
                        file.isUploaded = true
                        file.file.id = res.id
                        return res
                    })
                    .catch((err) => {
                        file.errorMessage = err.response.data.message || i18n.t('common.upload.unsupported')
                    })
            }),
        )
    },

    getDocumentType() {
        return axios.get(`/patient-records/types`)
    },

    getProviderFromNetwork(query, limit, offset) {
        return axios.get(`/provider/search`, {
            params: {
                query,
                limit,
                offset,
            },
        })
    },

    uploadDocuments(payload) {
        return axios.post(`/patient/documents/upload`, payload)
    },

    updateDocuments(payload) {
        return axios.put(`/patient/documents/update`, payload)
    },

    shareDocuments({ patientId, documentIds, recipientEmail, recipientType }) {
        return axios.post(`/patient/documents/share`, { patientId, documentIds, recipientEmail, recipientType })
    },

    getDocumentShares(patientId) {
        return axios.get(`/patient/${patientId}/documents/shares`)
    },

    revokeDocumentShare(shareId) {
        return axios.put(`/patient/documents/share/${shareId}/revoke`)
    },

    getSources(patientId, { fields } = {}) {
        const _fields = (fields || ['id', 'name']).join(',')
        return axios.get(`/patient/${patientId}/documents/sources`, {
            params: { _fields },
        })
    },

    getDocuments(patientId, { fields, limit, offset, ...queryParams } = {}) {
        const _fields = (
            fields || [
                'id',
                'fileName',
                'size',
                'note',
                'date',
                'recordType(id,name)',
                'source(id,name,userId,practiceId)',
                'url',
                'fromProviderId',
                'fromPracticeId',
                'providerSourceId',
                'isExpired',
                'expiresAt',
                'status',
                'isMcardSnapshot',
            ]
        ).join(',')
        return axios.get(`/patient/${patientId}/documents`, {
            params: { _fields, limit, offset, ...queryParams },
        })
    },

    getSingleDocument(patientId, documentId, { fields } = {}) {
        const _fields = (
            fields || [
                'id',
                'fileName',
                'name',
                'size',
                'note',
                'date',
                'recordType(id,name)',
                'source(id,name,userId,practiceId)',
                'url',
                'fromProviderId',
                'fromPracticeId',
                'providerSourceId',
                'isExpired',
                'expiresAt',
                'status',
                'isMcardSnapshot',
            ]
        ).join(',')
        return axios.get(`/patient/${patientId}/document/${documentId}`, {
            params: { _fields },
        })
    },

    deleteDocument(documentId, userId) {
        return axios.delete(`/patient/document/${documentId}/user/${userId}`)
    },

    getProviderPatientConnections(patientId, { fields } = {}, { limit, offset } = {}) {
        const _fields = (
            fields || [
                'user(id,email,firstName,lastName,designation,specialities(speciality(id,name),subSpeciality(id,name)),avatar(thumb-360,source),phone),practice(id,practiceType,name,address,city,state,zip,phone,email)',
            ]
        ).join(',')
        return axios.get(`/patient/${patientId}/connections`, {
            params: { _fields, limit, offset },
        })
    },
}
